/**
 * Definitions of types, enums, or constants for modules.
 *
 * Copyright © Ircam Amplify 2025
 */

import { logger } from '@/config/logger'

/**
 * The known channel mode.
 */
export enum ChannelMode {
  Mono = 'mono',
  Stereo = 'stereo',
}

/**
 * Convert a list of ChannelMode values to a list of numbers
 * @param aChannelModeList The list of ChannelMode.
 * @returns A list of numbers of channels or undefined.
 */
export const toChannelCountList = (
  aChannelModeList: ChannelMode[] | undefined
): number[] | undefined => {
  if (!aChannelModeList) {
    return undefined // Return undefined if the input is null or undefined
  }

  const channelCountList: number[] = []

  for (const mode of aChannelModeList) {
    switch (mode) {
      case ChannelMode.Mono:
        channelCountList.push(1)
        break
      case ChannelMode.Stereo:
        channelCountList.push(2)
        break
      default:
        // Optional: Handle unexpected values
        logger.warning(`Unexpected channel mode: ${mode}`)
    }
  }

  return channelCountList
}

export type ModuleCategory = 'MUSIC' | 'VOICE' | 'TOOLS'

export type ModuleMimeType =
  | 'audio/aac'
  | 'audio/aiff'
  | 'audio/flac'
  | 'audio/mpeg'
  | 'audio/wav'
  | 'audio/x-m4a'
  | 'audio/x-ms-wma'
  | 'audio/x-wav'
  | 'text/plain'

export enum InputCondition {
  Default,
  Mandatory,
  Optional,
}

type FileFormat =
  | 'aac'
  | 'aiff'
  | 'flac'
  | 'm4a'
  | 'mp3'
  | 'txt'
  | 'wav'
  | 'wma'

export type CostCriterion = 'api_call' | 'audio_duration'

/**
 * A dictionary mapping file formats to corresponding mime types.
 */
export const fileFormatToMime: Record<FileFormat, ModuleMimeType[]> = {
  aac: ['audio/aac'],
  aiff: ['audio/aiff'],
  flac: ['audio/flac'],
  m4a: ['audio/x-m4a'],
  mp3: ['audio/mpeg'],
  txt: ['text/plain'],
  wav: ['audio/wav', 'audio/x-wav'],
  wma: ['audio/x-ms-wma'],
}

export const mimeToFileFormat: Record<ModuleMimeType, FileFormat> = {
  'audio/aac': 'aac',
  'audio/aiff': 'aiff',
  'audio/mpeg': 'mp3',
  'audio/flac': 'flac',
  'audio/wav': 'wav',
  'audio/x-m4a': 'm4a',
  'audio/x-ms-wma': 'wma',
  'audio/x-wav': 'wav',
  'text/plain': 'txt',
}

export const safariSupportedMimeList = [
  'audio/aac',
  'audio/mpeg',
  'audio/flac',
  'audio/wav',
  'audio/x-wav',
  'audio/x-m4a',
]

/**
 * Returns a list of mime types, given a list of file formats.
 * @param formatList The list of file formats.
 * @returns A list of mime types.
 */
export const getMimeTypeFromFileFormatList = (
  formatList: FileFormat[]
): ModuleMimeType[] => {
  return formatList.flatMap((f) => fileFormatToMime[f])
}

/**
 * Properties for the file requirements of a module.
 */
export type FileFieldConfig = {
  /**
   * The list of supported file formats.
   */
  formats: FileFormat[]

  /**
   * The minimum duration of the supported files, in number of seconds.
   */
  minimumDurationSeconds?: number

  /**
   * The maximum duration of the supported files, in number of seconds.
   */
  maximumDurationSeconds?: number

  /**
   * The supported channel mode.
   */
  channelModes?: ChannelMode[]

  /**
   * Optional information.
   */
  additionalInfo?: string
}
